import { Modal } from '@/components/modal/modal';
import { Field } from '@/components/ui/field';
import { toaster } from '@/components/ui/toaster';
import { useShopifyStore } from '@/stores/integrations/shopify-store';
import { Box, Button, FieldHelperText, Input, Stack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { InputGroup } from '../ui/input-group';

const createFormSchema = (t: (key: string) => string) =>
  z.object({
    shopUrl: z
      .string()
      .min(1, t('integrations.shopify.errors.storeUrlRequired'))
      .regex(/^[a-zA-Z0-9-]+$/, t('integrations.shopify.errors.storeUrlInvalid')),
  });

type FormData = z.infer<ReturnType<typeof createFormSchema>>;

interface ShopifyStoreUrlModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ShopifyStoreUrlModal = ({ isOpen, onClose }: ShopifyStoreUrlModalProps) => {
  const { t } = useTranslation();
  const { connect } = useShopifyStore();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(createFormSchema(t)),
    defaultValues: {
      shopUrl: '',
    },
  });

  const onSubmit = async (data: FormData) => {
    try {
      await connect(data.shopUrl);
      toaster.create({
        description: t('integrations.shopify.errors.redirecting'),
        type: 'loading',
      });
      onClose();
    } catch (error) {
      toaster.create({
        description: t('integrations.shopify.errors.connectionFailed'),
        type: 'error',
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('integrations.shopify.modal.title')}>
      <Box as="form" onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={4}>
          <Field label={t('integrations.shopify.modal.label')} errorText={errors.shopUrl?.message}>
            <InputGroup endElement=".myshopify.com" w="full">
              <Input
                placeholder={t('integrations.shopify.modal.placeholder')}
                {...register('shopUrl')}
                pr="120px"
              />
            </InputGroup>
            <FieldHelperText mt={2} fontSize="sm" color="gray.600">
              {t('integrations.shopify.modal.helperText')}
            </FieldHelperText>
          </Field>
          <Box display="flex" justifyContent="flex-end">
            <Button variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button ml={2} type="submit">
              {t('integrations.card.connect')}
            </Button>
          </Box>
        </Stack>
      </Box>
    </Modal>
  );
};

export default ShopifyStoreUrlModal;
